import React, { useEffect, useState } from 'react'
import clsx from 'clsx'
import { IcCalendar, IcChervonDown, IcClose, IcUsers } from '../common/Icons'
import BasicInput2 from '../ui/BasicInput2'
import BasicButton from '../ui/BasicButton'
import { DATE_FORMAT, VIEW } from '../../constant'
import { useAppDispatch, useAppSelector } from '../../redux/hooks'
import { getCalendar, getPricesBooking } from '../../api/common'
import { setBookingPrices, setIsBookingAvail, setPromoCode, updateDataCalendar } from '../../redux/slices/property'
import { renderDates } from '../../utils'
import { FormError } from '../../interfaces'
import PriceBreakDown from '../checkout/PriceBreakDown'
import moment, { Moment } from 'moment'
import { setToastError } from '../../redux/slices/common'

interface Props {
  hostId: string
  updateView: (_v: string) => void
}

const ListingDetail: React.FC<Props> = ({ hostId, updateView }) => {
  const dispatch = useAppDispatch()
  const { isLayoutHasScroll, cryptoPayment, view } = useAppSelector((state) => state.common)
  const { adults, children, infants, pets, checkIn, checkOut, listingData, isBookingAvail, promoCode, bookingPrices } =
    useAppSelector((state) => state.property)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  // const [currency] = useState('USD')
  const [inputPromoCode, setInputPromoCode] = useState<string>('')
  const [errorPromoCode, setErrorPromoCode] = useState<FormError>({ show: false, message: '' })
  const [nights, setNights] = useState<number>(1)
  const numberOfGuest = adults + children + infants
  const currency = listingData?.propertyPrice?.currency

  useEffect(() => {
    async function fetchCalendar(hostId: string, listingId: string, fromDate: string, toDate: string) {
      try {
        // setIsLoading(true)
        const res = await getCalendar(hostId, listingId, fromDate, toDate)
        if (Array.isArray(res.data)) {
          let _dataCalendar: any = {}
          for (let item of res.data) {
            _dataCalendar[item.date] = item
          }
          dispatch(updateDataCalendar(_dataCalendar))
        }
      } finally {
        setIsLoading(false)
      }
    }
    if (hostId && listingData?.id) {
      const currentDay: Moment = moment()
      const endDayOfNextMonth: Moment = moment().add(3, 'months').endOf('months')
      fetchCalendar(hostId, listingData?.id, currentDay.format(DATE_FORMAT), endDayOfNextMonth.format(DATE_FORMAT))
    }
  }, [hostId, listingData?.id])

  useEffect(() => {
    const checkInDate = moment(checkIn, 'YYYY-MM-DD')
    const checkOutDate = moment(checkOut, 'YYYY-MM-DD')
    const diffDays = checkOutDate.diff(checkInDate, 'days')
    setNights(diffDays)
  }, [checkIn, checkOut])

  useEffect(() => {
    if (bookingPrices) {
      setInputPromoCode(bookingPrices.promoCode?.code || '')
    }
  }, [view, bookingPrices])

  async function fetchPricesBooking() {
    if (checkIn && checkOut) {
      try {
        dispatch(setIsBookingAvail(true))
        setIsLoading(true)
        const res: any = await getPricesBooking({
          checkinDate: checkIn,
          checkoutDate: checkOut,
          adults: adults,
          children: children,
          infants: infants,
          pets: pets,
          listingId: listingData.id,
          currency: cryptoPayment || currency,
          promoCode: promoCode,
        })
        if (res.success) {
          const isAvail = res.data?.isAvail
          dispatch(setBookingPrices(res.data))
          dispatch(setIsBookingAvail(isAvail))
        }
      } catch (error: any) {
        // console.log('--er--', error.error.message)
        if (
          error.error.message.includes('The promo code you have entered is invalid') ||
          error.error.message.includes(
            'The promo code you have entered is invalid. Please verify the code and try again.'
          )
        ) {
          setErrorPromoCode({
            show: true,
            message: String(error.error.message),
          })
        } else {
          dispatch(setToastError({ message: error.error.message }))
        }
      } finally {
        setIsLoading(false)
      }
    }
  }

  useEffect(() => {
    fetchPricesBooking()
  }, [checkIn, checkOut, adults, children, infants, pets, listingData?.id, currency, promoCode, cryptoPayment])

  const handleReserve = async () => {
    updateView(VIEW.SELECT_PAYMENT)
  }

  const onChangePromoCode = (value: string) => {
    // dispatch(setPromoCode(value))
    setInputPromoCode(value)
    setErrorPromoCode({ show: false, message: '' })
  }

  const onApplyPromoCode = () => {
    dispatch(setPromoCode(inputPromoCode))
  }

  return (
    <div className={''}>
      <div className={'flex dtravel-flex-col gap-[16px]'}>
        <button
          className={clsx(
            'h-[48px] px-[14px] shadow-xs bg-white border border-neutral-300 dtravel-rounded-12',
            'dtravel-center-horizontal dtravel-gap-8 dtravel-w-full'
          )}
          onClick={() => {
            updateView(VIEW.SELECT_DATES)
          }}
        >
          <span className={'min-w-[24px]'}>
            <IcCalendar />
          </span>
          <span className={'dtravel-flex-1 text-left font-inter-400 text-14-20 text-neutral-800'}>
            {renderDates(checkIn, checkOut)}
          </span>
          <span className={'min-w-[24px]'}>
            <IcChervonDown />
          </span>
        </button>
        <button
          className={clsx(
            'h-[48px] px-[14px] shadow-xs bg-white border border-neutral-300 dtravel-rounded-12',
            'dtravel-center-horizontal dtravel-gap-8 dtravel-w-full'
          )}
          onClick={() => {
            updateView(VIEW.SELECT_GUEST)
          }}
        >
          <span className={'min-w-[24px]'}>
            <IcUsers />
          </span>
          <span className={'dtravel-flex-1 text-left font-inter-400 text-14-20 text-neutral-800'}>
            {numberOfGuest > 0 ? `${numberOfGuest} ${numberOfGuest > 1 ? 'guests' : 'guest'}` : 'Choose guests...'}
          </span>
          <span className={'min-w-[24px]'}>
            <IcChervonDown />
          </span>
        </button>
      </div>

      {/* ----Input Promo Code-----*/}
      <div className={'mt-[24px] flex items-start gap-[8px]'}>
        <div className={'flex-1'}>
          <BasicInput2
            label={'Promo code'}
            placeholder={'Add a promo code...'}
            value={inputPromoCode}
            onChange={(event) => onChangePromoCode(event.target.value)}
            endAdornment={
              <button
                className={'dtravel-absolute right-[8px] top-1/2 w-[24px] h-[24px]'}
                onClick={() => onChangePromoCode('')}
              >
                <IcClose />
              </button>
            }
            error={errorPromoCode}
          />
        </div>
        <div className={'w-auto'}>
          <p
            className={'dtravel-inter-500 dtravel-text-14-18 dtravel-neutral-900'}
            style={{ marginBottom: 8, display: 'block' }}
          >
            &nbsp;
          </p>
          <BasicButton
            variant={'contained'}
            onClick={onApplyPromoCode}
            disabled={!inputPromoCode || inputPromoCode === promoCode}
          >
            {promoCode && inputPromoCode === promoCode ? 'Applied' : 'Apply'}
          </BasicButton>
        </div>
      </div>

      <div className={'mt-[24px]'}>
        {bookingPrices && bookingPrices.exchangeRate && (
          <PriceBreakDown data={bookingPrices} currency={currency} nights={nights} />
        )}
      </div>
      {!isBookingAvail && (
        <p className="dtravel-inter-400 text-red-700 dtravel-text-12-16 bg-red-100 dtravel-rounded-12 p-3 mt-4">
          Unfortunately the requested time slot is not available for reservation. Change your selected time to book
          this.
        </p>
      )}

      <div className={isLayoutHasScroll ? 'h-[24px]' : 'h-[72px]'} />
      <div
        className={clsx('px-[24px] py-[16px] bg-white h-[72px] shadow-xl', {
          'dtravel-absolute bottom-0 left-0 dtravel-w-full': !isLayoutHasScroll,
          'sticky bottom-[-24px] left-0 !w-[calc(100%+48px)] ml-[-24px] mb-[-24px]': isLayoutHasScroll,
        })}
        style={{ zIndex: 1200 }}
      >
        <BasicButton
          variant={'contained'}
          color={'black'}
          clases={'dtravel-w-full'}
          size={'lg'}
          onClick={handleReserve}
          loading={isLoading}
          disabled={!checkIn || !checkOut || numberOfGuest <= 0 || !isBookingAvail}
        >
          Reserve
        </BasicButton>
      </div>
    </div>
  )
}

export default ListingDetail
